import React from 'react';

interface IconProps {
  size?: number;
  className?: string;
}

const base = (size = 18) => ({
  width: size,
  height: size,
  viewBox: '0 0 20 20' as const,
  fill: 'none' as const,
  stroke: 'currentColor' as const,
  strokeWidth: '1.5' as const,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
});

export const DashboardIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="11" y="3" width="6" height="6" rx="1" />
    <rect x="3" y="11" width="6" height="6" rx="1" />
    <rect x="11" y="11" width="6" height="6" rx="1" />
  </svg>
);

export const AssistantIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path
      d="M9.5 3.5l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1 1-2.5z"
      fill="currentColor"
      stroke="none"
    />
    <path
      d="M14.5 11.5l.75 1.75 1.75.75-1.75.75-.75 1.75-.75-1.75-1.75-.75 1.75-.75.75-1.75z"
      fill="currentColor"
      stroke="none"
    />
    <circle cx="5" cy="14.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const StrategyIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="10" cy="10" r="7" />
    <circle cx="10" cy="10" r="3.5" />
    <circle cx="10" cy="10" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const PillarsIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M2 15l8 3.5 8-3.5" />
    <path d="M2 10l8 3.5 8-3.5" />
    <path d="M2 5l8 3.5 8-3.5" />
  </svg>
);

export const PlansIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="3" y="4" width="14" height="13" rx="2" />
    <path d="M3 9h14" />
    <path d="M7 4V2M13 4V2" />
    <path d="M7 13h2M11 13h2" />
  </svg>
);

export const ContentIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M14.5 2.5l3 3-9 9H5.5v-3l9-9z" />
    <path d="M12.5 4.5l3 3" />
    <path d="M3 17.5h14" />
  </svg>
);

export const ScheduleIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 5.5v5l3 3" />
  </svg>
);

export const MenuIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M3 5h14M3 10h14M3 15h14" />
  </svg>
);

export const LogoutIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M13 4.5l4.5 5.5-4.5 5.5" />
    <path d="M17.5 10H8" />
    <path d="M8 17.5H4a.5.5 0 01-.5-.5V3A.5.5 0 014 2.5h4" />
  </svg>
);

export const SettingsIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="10" cy="10" r="2.5" />
    <path d="M10 2.5v1.2M10 16.3v1.2M2.5 10h1.2M16.3 10h1.2M4.6 4.6l.85.85M14.55 14.55l.85.85M4.6 15.4l.85-.85M14.55 5.45l.85-.85" />
  </svg>
);

export const CalendarIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="3" y="4" width="14" height="13" rx="2" />
    <path d="M3 9h14" />
    <path d="M7 4V2M13 4V2" />
    <circle cx="7" cy="13" r="1" fill="currentColor" stroke="none" />
    <circle cx="10" cy="13" r="1" fill="currentColor" stroke="none" />
    <circle cx="13" cy="13" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const MediaIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="2" y="4" width="16" height="12" rx="2" />
    <circle cx="7.5" cy="9" r="1.5" />
    <path d="M2 14l4-4 3 3 3-4 6 6" />
  </svg>
);

export const SupervisorIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M10 2.5l6 2.6v4.2c0 3.5-2.4 6-6 8.2-3.6-2.2-6-4.7-6-8.2V5.1l6-2.6z" />
    <path d="M7.5 10l1.8 1.8L13 8" />
  </svg>
);

export const EngagementIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M3 5.5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H7l-3 2.5V5.5z" />
    <path d="M13 8.5h2a2 2 0 0 1 2 2V15l-2.5-2H10a2 2 0 0 1-1.8-1.1" />
  </svg>
);

export const TasksIcon = ({ size, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="4" y="3.5" width="12" height="14" rx="2" />
    <path d="M7.5 3.5V2.5h5v1" />
    <path d="M7 10.5l2 2 4-4.5" />
  </svg>
);
